<template>
  <BittsDropdown
    ref="dropdownRef"
    :disabled="!checkedReports.length"
    @visibility-changed="(viz: boolean) => (isMoveReportsOpen = viz)"
  >
    <template #prompt>
      <BittsFilterButton
        :disabled="!checkedReports.length"
        label-text="Actions"
        :hide-icon="true"
        variant="default"
        :is-open="isMoveReportsOpen"
      />
    </template>
    <template #content>
      <FolderDropdown
        :folders="dropdownFolders"
        :move-to-unfiled="moveToUnfiled"
        :can-share="canShareReports"
        :can-share-all="canShareAllChecked"
        :can-delete-all="canDeleteAllChecked"
        @item-selected="onFolderSelected"
        @delete-reports="onReportDeleteSelected"
        @share-reports="onShareSelected"
      />
    </template>
  </BittsDropdown>
  <DeleteReportModal
    v-if="showDeleteModal"
    :visible="showDeleteModal"
    :parent-loading="deleteModalLoading"
    :no-redirect="true"
    :num-lists="checkedReports.length"
    @reports-deleted="onDeleteReports"
    @closed="onDeleteModalClosed"
  />
  <BulkShareReportModal
    v-if="showShareModal"
    :visible="showShareModal"
    :report-ids="checkedReports"
    @shared="onShareComplete"
    @closed="showShareModal = false"
  />
</template>

<script setup lang="ts">
import { BittsDropdown, BittsFilterButton } from '@crossbeam/bitts';

import { storeToRefs } from 'pinia';
import { computed, ref } from 'vue';
import { useRoute } from 'vue-router';

import FolderDropdown from '@/components/reports/FolderDropdown.vue';

import { crossbeamApi } from '@/api';
import useAuth from '@/composables/useAuth';
import {
  getReportIsOwner,
  getReportUserAccessLevel,
} from '@/composables/useReportPermissions';
import { useFlashesStore, useReportsStore } from '@/stores';
import { ReportFolder } from '@/types/reports';

import BulkShareReportModal from './BulkShareReportModal.vue';
import DeleteReportModal from './DeleteReportModal.vue';

const { checkedReports = [], moveToUnfiled = false } = defineProps<{
  checkedReports?: string[];
  moveToUnfiled?: boolean;
}>();

const emit = defineEmits([
  'reports-moved',
  'show-new-folder-modal',
  'delete-cancelled',
]);
const isMoveReportsOpen = ref(false);

const route = useRoute();

const currentFolderUuid = computed(() => route.params.folder_id);
const dropdownFolders = computed(() =>
  folders.value.filter((folder) => folder.id !== currentFolderUuid.value),
);

const reportsStore = useReportsStore();
const { folders, reports } = storeToRefs(reportsStore);

const flashesStore = useFlashesStore();

const { hasScope, isAdmin, currentUser, currentOrg } = useAuth();
const canShareReports = computed(() => hasScope('write:share-reports'));
const checkedReportObjects = computed(() =>
  reports.value.filter((r) => checkedReports.includes(r.id)),
);

const canShareAllChecked = computed(() => {
  if (!canShareReports.value) return false;
  const userId = currentUser.value?.id;
  const orgId = currentOrg.value?.id;
  return checkedReportObjects.value.every(
    (report) =>
      getReportIsOwner(report, userId, orgId) ||
      isAdmin.value ||
      getReportUserAccessLevel(report, userId, orgId) === 'manager',
  );
});

const canDeleteAllChecked = computed(() => {
  const userId = currentUser.value?.id;
  const orgId = currentOrg.value?.id;
  return checkedReportObjects.value.every((report) => {
    if (getReportIsOwner(report, userId, orgId) || isAdmin.value) return true;
    const level = getReportUserAccessLevel(report, userId, orgId);
    if (level === 'commenter' || level === 'viewer') return false;
    return (
      report.visibility === 'public' ||
      level === 'editor' ||
      level === 'manager'
    );
  });
});

async function onFolderSelected(folder: ReportFolder) {
  if (folder.id === 'move-to-unfiled') {
    const currentFolder = folders.value.find(
      (folder) => folder.id === currentFolderUuid.value,
    );
    const filteredReports = currentFolder?.reports.filter(
      (reportUuid: string) => !checkedReports.includes(reportUuid),
    );
    if (!currentFolder?.id) return;
    const { error } = await crossbeamApi.PATCH('/v0.1/report-folders', {
      body: {
        id: currentFolder.id,
        name: currentFolder.name,
        reports: filteredReports,
      },
    });
    if (error) {
      flashesStore.addErrorFlash({
        message: 'Something went wrong',
        description: 'Lists not moved',
      });
      return;
    }
    flashesStore.addSuccessFlash({
      message: 'Lists moved to Unfiled',
    });
    emit('reports-moved');
  } else if (folder.id === 'new-folder') {
    emit('show-new-folder-modal');
    // we don't want to refresh for this, so returning early
    return;
  } else {
    const { error } = await crossbeamApi.PATCH('/v0.1/report-folders', {
      body: {
        id: folder.id,
        name: folder.name,
        reports: [...folder.reports, ...checkedReports],
      },
    });
    if (error) {
      flashesStore.addErrorFlash({
        message: 'Something went wrong',
        description: 'Lists not moved',
      });
      return;
    }
    flashesStore.addSuccessFlash({
      message: `Lists moved to ${folder.name}`,
    });
    emit('reports-moved');
  }
  await reportsStore.refreshReportsStore();
}

const dropdownRef = ref<InstanceType<typeof BittsDropdown> | null>(null);
const showDeleteModal = ref(false);
const deleteModalLoading = ref(false);
function onReportDeleteSelected() {
  if (dropdownRef.value) dropdownRef.value.isVisible = false;
  showDeleteModal.value = true;
}
async function onDeleteReports() {
  deleteModalLoading.value = true;
  try {
    const { error } = await crossbeamApi.DELETE('/v0.3/reports/bulk-delete', {
      body: { report_uuids: checkedReports },
    });
    if (error) {
      throw error;
    }
    flashesStore.addSuccessFlash({
      message: 'Lists deleted successfully',
    });
    emit('reports-moved');
    await reportsStore.refreshReportsStore();
  } catch (_err) {
    flashesStore.addErrorFlash({
      message: 'Something went wrong',
      description: 'Lists not deleted',
    });
  } finally {
    deleteModalLoading.value = false;
    showDeleteModal.value = false;
  }
}

function onDeleteModalClosed() {
  showDeleteModal.value = false;
  emit('delete-cancelled');
}

const showShareModal = ref(false);
function onShareSelected() {
  if (dropdownRef.value) dropdownRef.value.isVisible = false;
  showShareModal.value = true;
}
async function onShareComplete() {
  showShareModal.value = false;
  emit('reports-moved');
  await reportsStore.refreshReportsStore();
}

defineExpose({ canShareReports, showShareModal });
</script>
